/**
 * Aspect — Plan editor undo/redo history (pure state, no DOM)
 * --------------------------------------------------------------
 * Bounded snapshot stack extracted from plan_editor.js. Snapshots are opaque
 * strings (the editor serializes its model); the history only manages ordering,
 * deduping, the redo tail, and the size cap.
 */

const MAX_STEPS = 80;

export class PlanHistory {
  constructor(max = MAX_STEPS) {
    /** @type {string[]} */
    this.steps = [];
    this.idx = -1;
    this.max = max;
  }

  /** Replace all history with a single baseline snapshot (or empty). */
  reset(snapshot = null) {
    this.steps = snapshot ? [snapshot] : [];
    this.idx = snapshot ? 0 : -1;
  }

  /**
   * Record a snapshot AFTER a discrete edit. Truncates the redo tail and caps
   * the stack. No-op when the snapshot equals the current step.
   */
  record(snapshot) {
    if (!snapshot) return false;
    if (this.idx >= 0 && this.steps[this.idx] === snapshot) return false;
    this.steps.length = this.idx + 1; // drop redo tail
    this.steps.push(snapshot);
    if (this.steps.length > this.max) this.steps.splice(0, this.steps.length - this.max);
    this.idx = this.steps.length - 1;
    return true;
  }

  get canUndo() {
    return this.idx > 0;
  }

  get canRedo() {
    return this.idx < this.steps.length - 1;
  }

  /** @returns {string|null} snapshot to restore, or null when at the start */
  undo() {
    if (!this.canUndo) return null;
    this.idx -= 1;
    return this.steps[this.idx];
  }

  /** @returns {string|null} snapshot to restore, or null when at the end */
  redo() {
    if (!this.canRedo) return null;
    this.idx += 1;
    return this.steps[this.idx];
  }
}

export default PlanHistory;
